import React, {useEffect, useState} from 'react';
import {Outlet} from 'react-router-dom';
import {Box, CssBaseline} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import GlobalStyle from '../styles/global-style';
import Navbar from '../components/Navbar/Navbar';
import Sidebar from '../components/Sidebar/Sidebar';
import Footer from '../components/Footer/Footer';
import dashboardItems from '../routes/DashboardNavRoutes';
import breakpoints from '../theme/breakpoints';

// styles
import {
  Root,
  Drawer,
  AppContent,
  MainContent,
  MainContentWrapper,
} from './Dashboard.style';
import {useWindowDimensions} from '../utils';
import useAuth from '../hooks/useAuth';
import {SidebarItemsType} from '../types/typeSidebar';

const drawerWidth = 258;

const Dashboard: React.FC = ({children}) => {
  const {width} = useWindowDimensions();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [contentWidth, setContentWidth] = useState(width);
  const theme = useTheme();
  const {checkRbac} = useAuth();
  const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  useEffect(() => {
    setContentWidth(
      width > breakpoints.values.md ? width - drawerWidth : width
    );
  }, [width]);

  const getRbacDashboardItems = (items: SidebarItemsType[] = []) => {
    return (
      items
        .map((item: SidebarItemsType) => {
          // If item has children, process them first
          if (item?.children) {
            const filteredChildren = item.children
              .map((child: SidebarItemsType) => {
                // Recursively process any nested children
                if (child?.children) {
                  const nestedFilteredChildren = getRbacDashboardItems(
                    child.children
                  );

                  // If all nested children are filtered out, remove children property
                  if (nestedFilteredChildren.length === 0) {
                    // Create a shallow copy without children and subPage
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const {children, subPage, ...childWithoutChildren} = child;
                    return childWithoutChildren;
                  } else {
                    // Return with filtered children
                    return {
                      ...child,
                      children: nestedFilteredChildren,
                    };
                  }
                }
                return child;
              })
              // Filter children based on RBAC permissions
              .filter((child: SidebarItemsType) =>
                checkRbac({permissionString: child.requiredPermissions})
              );

            // If all children are filtered out, remove children and subPage properties
            if (filteredChildren.length === 0) {
              // Create a shallow copy without children and subPage
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const {children, subPage, ...itemWithoutChildren} = item;
              return itemWithoutChildren;
            } else {
              // Return with filtered children
              return {
                ...item,
                children: filteredChildren,
              };
            }
          }

          // Return the item as is if no children
          return item;
        })
        // Finally, filter out items the user doesn't have access to
        .filter((item: SidebarItemsType) =>
          checkRbac({permissionString: item.requiredPermissions})
        )
    );
  };

  // Filter dashboard items based on RBAC permissions
  const rbacDashboardItems = dashboardItems.map(item => {
    if (item.pages) {
      const validRbacPages = getRbacDashboardItems(item.pages)?.filter(page =>
        checkRbac({permissionString: page.requiredPermissions})
      );

      // Return a new item with only the valid pages
      return {
        ...item,
        pages: validRbacPages,
      };
    }

    return item;
  });

  return (
    <Root>
      <CssBaseline />
      <GlobalStyle />
      <Drawer className="no-print">
        <Box sx={{display: {xs: 'block', lg: 'none'}}}>
          <Sidebar
            PaperProps={{style: {width: drawerWidth}}}
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            items={rbacDashboardItems}
          />
        </Box>
        <Box sx={{display: {xs: 'none', md: 'block'}}}>
          <Sidebar
            PaperProps={{style: {width: drawerWidth}}}
            items={rbacDashboardItems}
          />
        </Box>
      </Drawer>
      <AppContent>
        <Navbar onDrawerToggle={handleDrawerToggle} />
        <Box width={contentWidth} style={MainContentWrapper}>
          <MainContent p={isLgUp ? 12 : 5}>
            {children}
            <Outlet />
          </MainContent>
        </Box>
        <Footer />
      </AppContent>
    </Root>
  );
};

export default Dashboard;
